// const eyes = require('eyes');

/*
  Initializes a single layout and then provides utility methods
*/
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import Atlas from './Atlas';

const _ = {
  cloneDeep,
  isEmpty,
};

const validEvents = ['click', 'blur', 'check', 'uncheck'];
const validEventActions = ['update'];

const validTypes = [
  'boolean',
  'color',
  'currency',
  'date',
  'element',
  'email',
  'file',
  'integer',
  'json',
  'number',
  'password',
  'percent',
  'phone',
  'section',
  'step',
  'string',
  'tab',
  'table',
  'time',
  'zipcode',
];

const numberFields = ['defaultSize', 'maxLen', 'maxListSize', 'maxlengthPerLine', 'minLen', 'minListSize'];

const validColKeys = ['xs', 'sm', 'md', 'lg', 'xl', 'xsOffset', 'smOffset', 'mdOffset', 'lgOffset', 'clear'];

const validDepActions = ['show', 'hide', 'enable', 'disable', 'entered', 'calculated'];

const validGroupKeys = {
  table: {
    canAdd: true,
    canBatchAdd: true,
    canDelete: true,
    canInsert: true,
    canSort: true,
    col: true,
    collapsibleRows: true,
    collapsibleText: true,
    defaultSize: true,
    dependencies: true,
    hideOnEmpty: true,
    insideStep: true,
    insideTab: true,
    key: true, // Added by Layout
    label: true,
    limit: true,
    maxListSize: true,
    minListSize: true,
    numberedRows: true,
    repeatHeaders: true,
    threshold: true,
    type: true,
  },
  section: {
    col: true,
    dependencies: true,
    insideStep: true,
    insideTable: true,
    insideTab: true,
    key: true,
    label: true,
    labelAlign: true,
    struct: true,
    style: true,
    type: true,
  },
};

const validFieldKeys = {
  aggregated: true,
  aggregate: true,
  allowNA: true,
  auto: true,
  autoPopulate: true,
  calculate: true,
  calculated: true,
  checkPage: true,
  clickToEdit: true,
  clearOnSubmit: true,
  control: true,
  col: true,
  collection: true,
  data: true,
  defaultSize: true,
  dependencies: true,
  disabled: true,
  displayOnly: true,
  events: true,
  fields: true,
  helpText: true,
  hidden: true,
  hideCheckAll: true,
  icon: true,
  identifier: true,
  info: true,
  insideStep: true,
  insideTable: true,
  insideTab: true,
  key: true,
  label: true,
  labelAlign: true,
  maxLen: true,
  maxLines: true,
  maxListSize: true,
  maxlengthPerLine: true,
  minLen: true,
  minListSize: true,
  name: true,
  noCopy: true,
  noEmptyOption: true,
  options: true,
  order: true,
  parentId: true,
  pattern: true,
  precision: true,
  predefined: true,
  props: true,
  placeholder: true,
  readOnly: true,
  readOnlyOnInit: true,
  required: true,
  style: true,
  target: true,
  toolbar: true,
  type: true,
  useOriginalName: true,
  validation: true,
  visibleLines: true,
  warning: true,

  // Table keys
  canAdd: true,
  canDelete: true,
  canInsert: true,
  limit: true,
  numberedRows: true,
  collapsibleRows: true,
  collapsibleText: true,
  repeatHeaders: true,
  threshold: true,

  width: true,

  // TODO don't like this here
  displayValue: true,
};

const booleanKeys = [
  'clearOnSubmit',
  'clickToEdit',
  'collapsibleRows',
  'collection',
  'disabled',
  'displayOnly',
  'hidden',
  'noEmptyOption',
  'numberedRows',
  'readOnly',
  'readOnlyOnInit',
  'repeatHeaders',
  'required',
  'useOriginalName',
];

class Layout {
  constructor(layout = [], id = '') {
    // Unique identifer for layout instance
    this.id = id || Layout.generateId();

    // allow valid controls to be extended
    this.validControls = [
      'Button',
      'Checkbox',
      'DbSelect',
      'Input',
      'Markdown',
      'MultiListSearch',
      'Paragraph',
      'ReactSelect',
      'Search',
      'Tags',
      'TagsMultiListSearch',
      'Text',
      'Textarea',
      'Upload',
    ];

    // Quick way to navigate through form objects
    this.atlas = new Atlas();

    this.load(layout);
  }

  load(layout) {
    // Page layout
    this.layout = _.cloneDeep(layout);

    this.ids = {};
    this.warnings = [];
    this.hasTabs = false;
    this.tabs = {};
    this.tabLayouts = {};
    this.hasSteps = false;
    this.steps = [];
    this.dependencyMap = {};
    this.prefix = '';

    // Flattened layout object by field name
    this.createLookup().validateFields();
  }

  /**
   * Creates a flatten object of the set layout.
   * i.e. { fieldName: { ...configuration... }}
   *
   * @return {Layout}
   */
  createLookup() {
    const layout = this.getLayout();
    const tabs = {};
    let order = 0;

    const crawl = (objects, parent) => {
      if (!Array.isArray(objects)) {
        this.warnings.push('Given layout is expected to be an array');
        return {};
      }

      let lookup = {};

      objects.forEach((field, index) => {
        if (!Layout.isObject(field)) {
          this.warnings.push(`All fields or groupings must be an object. (${index})`);
        }

        // Init Col shortcuts, this will modify the layout object
        if (field.col && !Layout.isObject(field.col)) {
          field.col = {
            sm: field.col,
            md: field.col,
            lg: field.col,
          };
        }

        // Build tab layout lookup object
        if (!parent || !parent.tabName) {
          if (field.type === 'tab') {
            this.tabLayouts[field.label || 'Main'] = field;
          } else if (!parent || !parent.parentType) {
            if (!this.tabLayouts.Main) {
              this.tabLayouts.Main = { type: 'tab', label: 'Main', fields: [] };
            }

            this.tabLayouts.Main.fields.push(field);
          }
        }

        if (Layout.isGroup(field.type)) {
          Layout.fixNumbers(field);
          Layout.initEvents(field);

          if (!field.fields || !Array.isArray(field.fields)) {
            this.warnings.push(`Field of type ${field.type} requires a fields key`);
          } else {
            if (field.type === 'step') {
              this.hasSteps = true;
              this.steps.push(field);
            }

            const parentId = this.generateUniqueName(field);

            let insideTable = field.type === 'table' ? parentId : null;
            if (parent && parent.insideTable) {
              insideTable = parent.insideTable; // eslint-disable-line
            }

            let insideStep = field.type === 'step' ? parentId : null;
            if (parent && parent.insideStep) {
              insideStep = parent.insideStep; // eslint-disable-line
            }

            // console.log(field);

            let insideTab = field.type === 'tab' ? parentId : null;
            if (parent && parent.insideTab) {
              insideTab = parent.insideTab; // eslint-disable-line
            }

            const parentInfo = {
              parentType: field.type,
              parentId,
              insideTable,
              insideStep,
              insideTab,
            };

            field.key = parentId;

            // Add insideStep on the Groups (and not itself)
            if (insideStep && insideStep !== field.key) {
              field.insideStep = insideStep;
            }

            // Add insideTable on the Groups (and not itself)
            if (insideTable && insideTable !== field.key) {
              field.insideTable = insideTable;
            }

            if (insideTab && insideTab !== field.key && field.type !== 'step') {
              field.insideTab = insideTab;
            }

            if (field.type === 'tab') {
              parentInfo.tabName = field.label || 'Main';
              this.hasTabs = true;
            }

            if (parent && parent.tabName) {
              parentInfo.tabName = parent.tabName;
            }

            this.atlas.add(parentId, parent ? parent.parentId : 'main');
            this.atlas.init(parentId);

            const { fields, ...rest } = field;
            lookup[parentId] = rest;

            const inner = crawl(fields, parentInfo);
            Object.keys(inner).forEach(key => {
              if (lookup[key]) {
                this.warnings.push(`Field with name ${key} already exists in ${field.type}`);
              }
            });

            lookup = { ...lookup, ...inner };
            return;
          }
        }

        if (lookup[field.name]) {
          this.warnings.push(`Field with name ${field.name} already exists (${field.type})`);
        }

        // Include a unique id for the parent
        if (parent) {
          if (parent.parentId) {
            field.parentId = parent.parentId;
            this.atlas.add(field.name, parent.parentId);
          }

          if (parent.insideTable) {
            field.insideTable = parent.insideTable;
            if (!field.aggregated) {
              field.collection = true;
            }
          }

          if (parent.insideStep) {
            field.insideStep = parent.insideStep;
          }

          if (parent.insideTab) {
            field.insideTab = parent.insideTab;
          }
        } else {
          this.atlas.add(field.name);
        }

        // Organize tab data to be easily looked up
        if (parent && parent.tabName) {
          if (!this.tabs[parent.tabName]) {
            this.tabs[parent.tabName] = [];
          }
          this.tabs[parent.tabName].push(field.name);
        } else {
          if (!this.tabs.Main) {
            this.tabs.Main = [];
          }
          this.tabs.Main.push(field.name);
        }

        field.identifier = this.generateFieldId(field.label);
        field.order = order;
        order += 1;

        Layout.fixNumbers(field);
        Layout.fixBooleans(field);
        Layout.initEvents(field);

        lookup[field.name] = field;

        if (field.aggregate) {
          const aggregateField = Layout.createAggregateField(field);
          aggregateField.identifier = this.generateFieldId(aggregateField.label);
          objects.push(aggregateField);
          aggregateField.parentId = parent.parentId;
          if (parent.insideTab) {
            aggregateField.insideTab = parent.insideTab;
          }
          if (parent.insideStep) {
            aggregateField.insideStep = parent.insideStep;
          }
          lookup[aggregateField.name] = aggregateField;
          this.atlas.add(aggregateField.name, parent.parentId);
        }
      });

      return lookup;
    };

    this.lookup = crawl(layout);
    // eyes.inspect(this.lookup);

    this.forEachTab((tabName, config) => {
      const { insideStep } = config;
      if (insideStep) {
        this.lookup[insideStep].hasTabs = true;
      }
    });

    if (Object.keys(tabs).length === 1 && Object.keys(tabs)[0] !== 'sticky') {
      this.warnings.push('Possibly unnecessary tabs set with only one unique page');
    }

    this.createDependencyMap();
    return this;
  }

  /**
   * Looks for and maps out any field to field dependencies within
   * different types of defined logic.
   *
   * @return {Layout}
   */
  createDependencyMap() {
    const lookup = this.getLookup();

    this.eachItem((fieldName, config) => {
      const { events, dependencies, calculate } = config;
      if (events) {
        validEvents.forEach(ev => {
          if (events[ev]) {
            validEventActions.forEach(action => {
              if (events[ev][action]) {
                events[ev][action] = Array.isArray(events[ev][action]) ? events[ev][action] : [events[ev][action]];
                events[ev][action].forEach(e => {
                  if (e.method && e.method.keys) {
                    e.method.keys.forEach(key => {
                      if (lookup[key]) {
                        this.addToDepMap(key, fieldName);
                      }
                    });
                  }
                });
              }
            });
          }
        });
      }

      if (dependencies) {
        const deps = Array.isArray(dependencies) ? dependencies : [dependencies];
        deps.forEach(dependency => {
          if (!Layout.isObject(dependency)) {
            this.warnings.push(`Dependency definition must be an object on ${fieldName}`);
            return;
          }
          Object.keys(dependency).forEach(logic => {
            if (logic === 'action') {
              if (!validDepActions.some(action => action === dependency.action)) {
                this.warnings.push(`Unknown action ${dependency.action} on ${fieldName}`);
              }
              return;
            }
            dependency[logic].forEach(rule => {
              if (!Layout.isObject(rule)) {
                this.warnings.push(`Dependency rule must be an object on ${fieldName}`);
                return;
              }
              Object.keys(rule).forEach(operator => {
                const values = rule[operator];
                values.forEach(value => {
                  // Value is a field name
                  if (lookup[value]) {
                    this.addToDepMap(value, fieldName);
                  }
                });
              });
            });
          });
        });
      }

      if (calculate) {
        if (!Layout.isObject(calculate)) {
          this.warnings.push(`Calculations must be an object on ${fieldName}`);
          return;
        }
        Object.keys(calculate).forEach(key => {
          if (key === 'params') {
            if (!Array.isArray(calculate.params)) {
              this.warnings.push(`Calculation params must be an array on ${fieldName}`);
              return;
            }
            calculate.params.forEach(param => {
              // Param is a field name
              if (lookup[param]) {
                this.addToDepMap(param, fieldName);
              }
            });
          }
        });
      }
    });

    return this;
  }

  static createAggregateField(field) {
    const name = `${field.name}-aggregate`;
    const data = '0';

    const calculate = Layout.isObject(field.aggregate)
      ? field.aggregate
      : {
          method: 'add',
          params: [field.name],
        };

    const label = `Total ${field.label}`;

    return {
      aggregated: true,
      calculate,
      calculated: true,
      collection: false,
      control: 'Input',
      data,
      displayValue: data,
      hidden: true,
      label,
      name,
      precision: field.precision,
      readOnly: true,
      type: field.type || 'number',
    };
  }

  /**
   * Adds a field to the dependency lookup.
   *
   * @param fieldA - Field in question.
   * @param fieldB - Fields dependent on its value or state.
   * @return {Layout}
   */
  addToDepMap(fieldA, fieldB) {
    if (!this.dependencyMap[fieldA]) {
      this.dependencyMap[fieldA] = [];
    }

    this.dependencyMap[fieldA].push(fieldB);
    return this;
  }

  /**
   * Deep validation of configuration object for every field looking for
   * issues.
   *
   * @return {Layout}
   */
  validateFields() {
    this.forEachField((fieldName, config) => {
      Object.keys(config).forEach(key => {
        const value = config[key];

        if (!validFieldKeys[key]) {
          this.warnings.push(`Unknown key: ${key}`);
        } else {
          switch (key) {
            case 'type': {
              if (!validTypes.some(type => type === value)) {
                this.warnings.push(`Unknown type ${value}`);
              }
              break;
            }
            case 'control': {
              if (
                !this.validControls.find(ctrl => ctrl.toLowerCase() === value.toLowerCase()) &&
                config.type !== 'element'
              ) {
                this.warnings.push(`Unknown control ${value}`);
              }
              break;
            }
            case 'validation': {
              if (Layout.isObject(value)) {
                Object.keys(value).forEach(valkey => {
                  if (valkey === 'optional' && typeof value[valkey] !== 'boolean') {
                    this.warnings.push(`Optional key in validation must be boolean: ${value[valkey]}`);
                  }
                });
              } else {
                this.warnings.push('Validation should be an object');
              }
              break;
            }
            case 'col': {
              this.validateColKey(value);
              break;
            }
          }
        }
      });
    });

    this.forEachGroup((groupName, config) => {
      this.validateGroupKeys(config);
    });

    this.checkForTabOrphans();

    return this;
  }

  /**
   * Shallow validation of group configs only
   *
   * @return {Layout}
   */
  validateGroupKeys(config) {
    const { type } = config;
    Object.keys(config).forEach(key => {
      if (validGroupKeys[type]) {
        if (!validGroupKeys[type][key]) {
          this.warnings.push(`Unknown ${key} for group type ${type}`);
        } else {
          switch (key) {
            case 'col': {
              this.validateColKey(config[key]);
              break;
            }
          }
        }
      }
    });

    return this;
  }

  /**
   * Validate the configuration of col
   *
   * @return {Layout}
   */
  validateColKey(value) {
    if (typeof value === 'object') {
      Object.keys(value).forEach(colkey => {
        if (!validColKeys.some(check => check === colkey)) {
          this.warnings.push(`Unknown col key ${colkey}`);
        } else if (typeof value[colkey] === 'number' && colkey !== 'clear') {
          if (value[colkey] > 12 || value[colkey] < 1) {
            this.warnings.push(`Col key '${colkey} value '${value[colkey]}' is not between 1 and 12`);
          }
        } else if (colkey === 'clear') {
          if (typeof value[colkey] !== 'boolean') {
            this.warnings.push(`Col key '${colkey} value '${value[colkey]} should be boolean`);
          }
        } else {
          this.warnings.push(`Col key '${colkey}' value '${value[colkey]}' should be a number between 1 and 12`);
        }
      });
    } else if (typeof value === 'number') {
      if (value > 12 || value < 1) {
        this.warnings.push(`Col value '${value}' is not between 1 and 12`);
      }
    } else {
      this.warnings.push(`Col value '${value}' should be a number between 1 and 12 or an object`);
    }

    return this;
  }

  /**
   * From the lookup an array previously used as the layout will be created.
   *
   * @return {Array}
   */
  getLegacyArray() {
    const gather = {};

    this.eachItem((fieldName, config) => {
      // Ignore groups
      if (this.atlas.groups[fieldName]) {
        return;
      }

      if (/^table/.test(config.parentId)) {
        if (!gather[config.parentId]) {
          gather[config.parentId] = [];
        }
        gather[config.parentId].push(this.lookup[fieldName]);
      } else {
        gather[fieldName] = config;
      }
    });

    return Object.keys(gather).map(fieldName => gather[fieldName]);
  }

  /**
   * Fill in values (initial values usually) into the layout itself and not just
   * the lookup object.
   *
   * Changes object looks like: { fieldName: fieldValue }
   *
   * @param {Object} changes - Configuration definitions.
   * @param {Function} conditional - Determines whether to set the value or not.
   * @return {Object}
   */
  populateLayout(changes = {}, conditional) {
    const layout = this.getLayout();

    const getSaneData = (field, data) => {
      if (field.type === 'boolean' || field.type === 'json' || field.type === 'file') {
        return data;
      }

      return data;
      // return String(data);
    };

    const crawl = fields => {
      fields.forEach(field => {
        if (Layout.isGroup(field.type)) {
          crawl(field.fields);
        }

        if (conditional) {
          if (!conditional(field)) {
            return;
          }
        }

        if (changes[field.name] !== undefined) {
          if (Layout.isObject(changes[field.name]) && field.type !== 'json') {
            Object.keys(changes[field.name]).forEach(key => {
              if (key === 'data') {
                field[key] = getSaneData(field, changes[field.name][key]);
              } else {
                field[key] = changes[field.name][key];
              }
            });
          } else {
            field.data = getSaneData(field, changes[field.name]);
          }
        }
      });
    };

    crawl(layout);
    return this;
  }

  /**
   * Mutates the event configuration for a field an ensures its sanity.
   *
   * @param {Object} field - Field object.
   * @return {Object} - The mutated events object.
   */
  static initEvents(field) {
    const { events } = field;
    if (_.isEmpty(events)) {
      return events;
    }

    const replaceFieldNameKeys = function(withThis) {
      this.field.forEach(f => {
        f.replace('#KEY#', withThis);
      });

      if (this.method) {
        this.keys.forEach(k => {
          k.replace('#KEY#', withThis);
        });
      }
    };

    validEvents.forEach(ev => {
      if (events[ev]) {
        validEventActions.forEach(action => {
          if (events[ev][action]) {
            events[ev][action] = Array.isArray(events[ev][action]) ? events[ev][action] : [events[ev][action]];

            events[ev][action].forEach(e => {
              if (!e.field) {
                e.field = [];
              }
              e.field = Array.isArray(e.field) ? e.field : [e.field];

              if (e.method) {
                // TODO: Rename keys to params
                if (!e.keys) {
                  e.keys = [];
                }
                e.keys = Array.isArray(e.keys) ? e.keys : [e.keys];
              }

              e.replaceFieldNameKeys = replaceFieldNameKeys.bind(e);
            });
          }
        });
      }
    });

    field.events = events;

    return this;
  }

  /**
   * Backwards compatibility for boolean fields.  The value 'yes' was used for
   * true values.  This does a convert plus a warning.
   *
   * @param {Object} field - Field configuration.
   * @return {Object}
   */
  static fixBooleans(field) {
    const baddies = [];

    booleanKeys.forEach(key => {
      if (field[key] !== undefined && typeof field[key] !== 'boolean') {
        field[key] = field[key] === 'yes';
        baddies.push(key);
      } else {
        field[key] = field[key] === true;
      }
    });

    if (field.calculate) {
      field.calculated = true;
    }

    if (baddies.length) {
      // Warning the issue to help find outliers
      // console.warn(`Booleans need fixing: ${field.name} on ${baddies.join(',')}`);
    }

    return field;
  }

  /**
   * Make sure data that should be numbers are numbers by javascript standards.
   *
   * @param {Object} field - Field configuration.
   * @return {Object}
   */
  static fixNumbers(field) {
    numberFields.forEach(key => {
      if (field[key]) {
        field[key] = parseInt(field[key], 10);
      }
    });

    return field;
  }

  /**
   * Map fields names to a different key
   *
   * @param {Object} mapping - Field configuration.
   * @return {Layout}
   */
  mapFieldKeys(mapping = {}) {
    const mappedLookup = {};

    this.eachItem((fieldName, config) => {
      const mappedField = {};
      Object.keys(config).forEach(key => {
        if (mapping[key]) {
          mappedField[mapping[key]] = config[key];
        }
        mappedField[key] = config[key];
      });
      mappedLookup[fieldName] = mappedField;
    });

    this.lookup = mappedLookup;
    return this;
  }

  /**
   * Print all warnings currently in the stack.
   *
   * @return {Layout}
   */
  printWarnings() {
    this.warnings.forEach(warning => {
      console.log(`[${this.id}] ${warning}`);
    });

    return this;
  }

  /**
   * Return an array of steps.
   *
   * @return {Array}
   */
  getSteps() {
    return this.steps;
  }

  /**
   * Get a list of fields for a step.
   *
   * @param {Number} step - Step number (not label)
   * @return {Array}
   */
  getStepFields(step = 0) {
    if (!this.steps[step] || !this.steps[step].fields || !Array.isArray(this.steps[step].fields)) {
      return [];
    }

    return this.steps[step].fields.map(field => field.name);
  }

  /**
   * Return an object wherein the keys are the names of the fields and their values
   * are object configs.
   *
   * @return {Object}
   */
  getLookup() {
    return this.lookup;
  }

  /**
   * Return the page layout that defines the instance.
   *
   * @return {Array}
   */
  getLayout() {
    return this.layout;
  }

  /**
   * Return mapping of field dependencies.
   *
   * @return {Object}
   */
  getDependencyMap() {
    return this.dependencyMap;
  }

  /**
   * Unique id for this layout instance.
   *
   * @return {number}
   */
  getId() {
    return this.id;
  }

  /**
   * Every field by name under a given tab.
   *
   * @return {Object}
   */
  getTabLookup() {
    return this.tabs;
  }

  /**
   * Every layout under a given tab.
   *
   * @return {Object}
   */
  getTabLayouts() {
    return this.tabLayouts;
  }

  /**
   * Returns an array of all input fields
   *
   * @return {Array}
   */
  getAllInputFields() {
    const fields = [];
    this.eachItem((fieldName, config) => {
      if (Layout.isGroup(config.type)) {
        return;
      }

      fields.push(fieldName);
    });

    return fields;
  }

  /**
   * Returns an array of all input fields under a Group
   *
   * @return {Array}
   */
  getAllFieldsUnderGroup(groupName) {
    let fields = [];

    if (!this.atlas.groups[groupName]) {
      console.warn(`Group ${groupName} doesn't exist.`);
    }

    this.atlas.groups[groupName].forEach(fieldName => {
      const field = this.lookup[fieldName];
      if (Layout.isGroup(field.type)) {
        const innerFields = this.getAllFieldsUnderGroup(fieldName);
        fields = [...fields, ...innerFields];
        return;
      }

      fields.push(fieldName);
    });

    return fields;
  }

  /**
   * Returns section lookup for a Table
   *
   * @return {Object}
   */
  getAllSectionsUnderTable(tableName) {
    const groups = {};

    if (!this.atlas.groups[tableName]) {
      console.warn(`Group ${groupName} doesn't exist.`);
    }

    this.atlas.groups[tableName].forEach(fieldName => {
      const field = this.lookup[fieldName];
      if (Layout.isGroup(field.type)) {
        groups[fieldName] = field;
      }
    });

    return groups;
  }

  traverseLayout(cb) {
    this.atlas.forEach(key => {
      cb(key, this.lookup[key]);
    });
  }

  checkForTabOrphans() {
    if (this.hasSteps) {
      this.forEachStep((stepName, config) => {
        if (config.hasTabs) {
          this.getAllFieldsUnderGroup(config.key).forEach(fieldName => {
            // Exceptions
            if (fieldName === 'itemid') {
              return;
            }
            const config = this.lookup[fieldName];
            if (!config.insideTab) {
              this.warnings.push(`Field ${fieldName} is a tab orphan`);
            }
          });
        }
      });
      return;
    }

    if (this.hasTabs && !this.hasSteps) {
      this.forEachField((fieldName, config) => {
        // Exceptions
        if (fieldName === 'itemid') {
          return;
        }
        if (!config.insideTab) {
          this.warnings.push(`Field ${fieldName} is a tab orphan`);
        }
      });
    }
  }

  /**
   * Generate a unique id for groups or the layout itself.
   *
   * @return {number}
   */
  static generateId() {
    return Math.random()
      .toString(36)
      .substr(2, 9);
  }

  /**
   * Creates a unique id for the field.  Using the field name would usually
   * make the most sense, but the field names are obscure and it can make writing
   * integration tests difficult.
   *
   * @param {String} label Field label.
   * @return {Boolean}
   */
  generateFieldId(label) {
    const name = label ? Layout.sanitizeKey(label) : 'Unlabeled';
    const id = `${this.prefix}${Layout.sanitizeKey(name)}`;
    let newId = id;

    let index = 0;
    while (this.ids[newId]) {
      newId = `${id}-${index}`;
      index += 1;
    }

    this.ids[newId] = true;
    return newId;
  }

  /**
   * Generate a unique name for groups.
   *
   * @param {Object} group - Group config.
   * @return {number}
   */
  generateUniqueName(group = {}) {
    let name = `${group.type}-${group.label ? Layout.sanitizeKey(group.label) : 'Unlabeled'}`;
    let count = 0;
    while (this.atlas.groups[name]) {
      count += 1;
      name = `${group.type}-${group.label ? Layout.sanitizeKey(group.label) : 'Unlabeled'}-${count}`;
    }
    return name;
  }

  /**
   * Helper method to loop through tables.
   *
   * @param {Function} cb - Callback
   */
  forEachField(cb) {
    this.eachItem((fieldName, config) => {
      if (Layout.isGroup(config.type)) {
        return;
      }

      cb(fieldName, config);
    });
  }

  /**
   * Helper method to loop through tables.
   *
   * @param {Function} cb - Callback
   */
  forEachTable(cb) {
    this.eachItem((fieldName, config) => {
      if (config.type !== 'table') {
        return;
      }

      cb(fieldName, config);
    });
  }

  /**
   * Helper method to loop through tabs.
   *
   * @param {Function} cb - Callback
   */
  forEachTab(cb) {
    this.eachItem((fieldName, config) => {
      if (config.type !== 'tab') {
        return;
      }

      cb(fieldName, config);
    });
  }

  /**
   * Helper method to loop through steps.
   *
   * @param {Function} cb - Callback
   */
  forEachStep(cb) {
    this.eachItem((fieldName, config) => {
      if (config.type !== 'step') {
        return;
      }

      cb(fieldName, config);
    });
  }

  /**
   * Helper method to loop through groups.
   *
   * @param {Function} cb - Callback
   */
  forEachGroup(cb) {
    this.eachItem((fieldName, config) => {
      if (!Layout.isGroup(config.type)) {
        return;
      }

      cb(fieldName, config);
    });
  }

  /**
   * Helper method to loop through each config.
   *
   * @param {Function} cb - Callback
   */
  eachItem(cb) {
    const lookup = this.getLookup();
    Object.keys(lookup).forEach(fieldName => {
      const config = lookup[fieldName];
      cb(fieldName, config);
    });
  }

  /**
   * Determine if a given type has a group of fields.
   *
   * @param {String} type - Form object type.
   * @return {Boolean}
   */
  static isGroup(type) {
    if (type === 'section' || type === 'table' || type === 'tab' || type === 'step') {
      return true;
    }

    return false;
  }

  /**
   * Helper function because typeof is not consistent in JS.
   *
   * @param {Mixed} item - Variable being evaluated.
   * @return {Boolean}
   */
  static isObject(item) {
    return typeof item === 'object' && !Array.isArray(item) && item !== null;
  }

  /**
   * Converts a string into a key that is happy(ier)
   * @param {String} key - Usually a field name with special chars.
   * @return {String} - Converted string
   */
  static sanitizeKey(key = '') {
    key = key
      .replace(/\*/g, 'STAR')
      .replace(/\./g, 'DOT')
      .replace(/~/g, 'TILDA')
      .replace(/\s/g, '')
      .replace(/,/g, 'AND')
      .replace(/\//g, 'SLASH')
      .replace(/\(/g, 'LP')
      .replace(/\)/g, 'RP')
      .replace(/#/g, 'HASH');

    return key;
  }
}

export default Layout;
